<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { z } from "zod";
import {
    UiButton,
    UiText,
    UiIcon,
    UiFormBuilder,
    useMediaUploadIntegration,
    useToaster,
    type BuilderRowConfig,
} from "@shopexperts/shared";
import { useClientProfile } from "@/app/composables/useClientProfile";
import { usePendingAvatarUpload } from "@/app/composables/usePendingAvatarUpload";
import { useAuthStore } from "@/store/auth";
import EmailChangeAside from "@/app/components/settings/EmailChangeAside.vue";

// Schema for the profile form
const personalDetailsSchema = z.object({
    first_name: z.string().max(255).optional(),
    last_name: z.string().max(255).optional(),
    company_name: z.string().max(255).optional(),
    company_website: z.string().max(500).optional().nullable(),
    avatar: z.object({
        file: z.any().optional(),
        url: z.string().optional(),
        media_id: z.string().optional(),
        uploadedAt: z.any().optional(),
        isTemporary: z.boolean().optional(),
    }).optional(),
});

type PersonalDetailsFormValues = z.infer<typeof personalDetailsSchema>;

const authStore = useAuthStore();
const { profile, user, client, initials, pendingEmailChange, updateProfile, requestEmailChange } = useClientProfile();
const { uploadFn, deleteFn } = useMediaUploadIntegration();
const toaster = useToaster();

// Profile form state
const model = ref<PersonalDetailsFormValues>();
const initialValues = ref<PersonalDetailsFormValues>();
const isDirty = ref(false);
const isValid = ref(true);
const isSaving = ref(false);
const avatar = computed(() => model.value?.avatar);
const { hasPendingUpload: hasAvatarPendingUpload, waitForMediaId: waitForAvatarMediaId } =
    usePendingAvatarUpload(avatar);

// Email change aside state
const emailAsideOpen = ref(false);

// Profile picture URL for display
const profilePictureUrl = computed(() => {
    if (client.value?.profile_picture_media?.cdn_url) {
        return client.value.profile_picture_media.cdn_url;
    }
    return client.value?.profile_picture || undefined;
});

// Sync profile form with profile data
watch(
    profile,
    (newProfile) => {
        if (newProfile) {
            const avatarItem = newProfile.client.profile_picture_media
                ? {
                    url: newProfile.client.profile_picture_media.cdn_url,
                    media_id: newProfile.client.profile_picture_media.id,
                    isTemporary: false,
                }
                : newProfile.client.profile_picture
                    ? { url: newProfile.client.profile_picture }
                    : undefined;

            initialValues.value = {
                first_name: newProfile.user.first_name || "",
                last_name: newProfile.user.last_name || "",
                company_name: newProfile.client.company_name || "",
                company_website: newProfile.client.company_website || "",
                avatar: avatarItem,
            };
        }
    },
    { immediate: true }
);

// Profile form rows configuration
const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            {
                name: "avatar",
                type: "avatar-upload",
                props: {
                    initials: initials.value,
                    initialUrl: profilePictureUrl.value,
                    autoUpload: true,
                    uploadFn,
                    deleteFn,
                    collection: "client_avatar",
                },
            },
        ],
    },
    {
        columns: [
            {
                name: "first_name",
                label: "First Name",
                type: "text",
                props: { placeholder: "Enter your first name" },
            },
            {
                name: "last_name",
                label: "Last Name",
                type: "text",
                props: { placeholder: "Enter your last name" },
            },
        ],
    },
    {
        columns: [
            {
                name: "company_name",
                label: "Company Name",
                type: "text",
                props: { placeholder: "Enter your company name" },
            },

        ],
    },
    {
        columns: [
            {
                name: "company_website",
                label: "Company Website",
                type: "url",
                props: { placeholder: "Enter your company website" },
            },
        ],
    }
]);

async function handleSaveProfile() {
    if (!model.value) return;

    isSaving.value = true;
    
    // Create a loading toast that we'll update as we progress
    const toastId = toaster.create({
        title: hasAvatarPendingUpload.value ? "Uploading image..." : "Saving profile...",
        type: "loading",
    });

    try {
        // Wait for any pending avatar upload to complete (file exists but no media_id)
        if (hasAvatarPendingUpload.value) {
            const uploadCompleted = await waitForAvatarMediaId();
            if (!uploadCompleted) {
                toaster.update(toastId, {
                    title: "Image upload timed out. Please try again.",
                    type: "error",
                });
                return;
            }
            // Update toast to show we're now saving
            toaster.update(toastId, {
                title: "Saving profile...",
                type: "loading",
            });
        }

        await updateProfile({
            first_name: model.value.first_name || undefined,
            last_name: model.value.last_name || undefined,
            company_name: model.value.company_name || undefined,
            company_website: model.value.company_website || undefined,
            profile_picture_id: model.value.avatar?.media_id || undefined,
        });
        
        toaster.update(toastId, {
            title: "Profile updated successfully",
            type: "success",
        });
        
        // Update auth store with new name for header
        if (authStore.user) {
            authStore.user.first_name = model.value.first_name;
            authStore.user.last_name = model.value.last_name;
        }
        // Update initialValues to match the saved model so isDirty stays in sync
        // This ensures the form knows what the new "clean" state is
        // We create a proper copy to avoid reference issues
        const savedAvatar = model.value.avatar?.media_id
            ? {
                url: model.value.avatar.url,
                media_id: model.value.avatar.media_id,
                isTemporary: false,
            }
            : model.value.avatar?.url
                ? { url: model.value.avatar.url }
                : undefined;

        initialValues.value = {
            first_name: model.value.first_name || "",
            last_name: model.value.last_name || "",
            company_name: model.value.company_name || "",
            company_website: model.value.company_website || "",
            avatar: savedAvatar,
        };
        // The watch will also update initialValues when profile changes from the API,
        // but we set it here immediately to keep isDirty accurate right away
    } catch (err: unknown) {
        toaster.update(toastId, {
            title: getErrorMessage(err, "Failed to update profile"),
            type: "error",
        });
    } finally {
        isSaving.value = false;
    }
}

function openEmailAside() {
    emailAsideOpen.value = true;
}

function getErrorMessage(err: unknown, fallback: string): string {
    const error = err as { response?: { data?: { message?: string } }; message?: string };
    return error?.response?.data?.message || error?.message || fallback;
}
</script>

<template>
    <div class="bg-white rounded-lg border border-gray-200 p-6 max-w-[720px] w-full">
        <UiFormBuilder v-if="initialValues" :schema="personalDetailsSchema" :rows="rows" :initial-values="initialValues"
            v-model="model" v-model:dirty="isDirty" v-model:valid="isValid" />

        <!-- Email Display -->
        <div class="mt-6 pt-6 border-t border-gray-200 flex flex-col">
            <div class="flex flex-col items-start justify-between">
                    <UiText variant="h6" as="label" class="block" text="Email" />
                    <div class="flex items-center">
                        <UiText variant="p" as="p" class="font-medium">
                            {{ user?.email }}
                        </UiText>
                        <UiButton variant="ghost" shape="square" @click="openEmailAside">
                            <template #before_label>
                                <UiIcon name="pencil" class="w-4 h-4" />
                            </template>
                        </UiButton>
                    </div>
            </div>
            <div v-if="pendingEmailChange" class="mt-2 flex items-center gap-2 text-amber-600">
                <UiIcon name="info-circle" class="w-4 h-4" />
                <UiText variant="caption" as="span">
                    Pending change to {{ pendingEmailChange.new_email }}
                </UiText>
            </div>
        </div>

        <!-- Save Button -->
        <div class="flex justify-end mt-6 pt-6 border-t border-gray-200">
            <UiButton variant="primary" label="Save Changes" :loading="isSaving" :disabled="!isDirty"
                @click="handleSaveProfile" />
        </div>

        <!-- Email Change Aside -->
        <EmailChangeAside v-model="emailAsideOpen" :request-email-change="requestEmailChange" />
    </div>
</template>
